import { createSynth, type Synth } from '../../audio/synth';
import type { Feedback } from '../types';

/**
 * Reward and redirection, for a room of four-year-olds.
 *
 * There is no failure state anywhere in this file, and that is the point. The
 * RPPH's note to the app team asks for positive visual and audio feedback on
 * every successful interaction "supaya anak tetap termotivasi tanpa merasa
 * 'gagal'". So the vocabulary here is `celebrate` and `nudge` - never `wrong`,
 * never `fail`. A tap that does not fit gets a gentle wobble and a soft tone
 * that says "try another one", not a red cross that says "you were wrong".
 *
 * DOM and CSS rather than the PlayCanvas confetti in `src/engine/`: this lane
 * renders as SVG and DOM, and pulling the 3D engine in for a star burst would
 * add roughly 450 kB to a lesson that otherwise needs none of it.
 */

/** Star burst particle count. Enough to read as a reward at three metres. */
const STARS = 14;
const STAR_LIFETIME_MS = 900;

export function createFeedback(host: HTMLElement): Feedback {
  const synth: Synth = createSynth();
  let disposed = false;

  const layer = document.createElement('div');
  layer.className = 'mod-feedback';
  // Purely decorative and absolutely positioned over the activity; it must
  // never intercept a tap meant for the screen underneath.
  layer.setAttribute('aria-hidden', 'true');
  host.append(layer);

  const centreOf = (target: HTMLElement | SVGElement) => {
    const box = target.getBoundingClientRect();
    const frame = layer.getBoundingClientRect();
    return { x: box.left - frame.left + box.width / 2, y: box.top - frame.top + box.height / 2 };
  };

  return {
    celebrate(target) {
      if (disposed) return;

      // A rising major arpeggio. Ascending reads as "yes" almost universally;
      // the same notes descending read as "no" even to a child who has never
      // heard the sound before.
      synth.sequence([4, 9, 12, 16], { stagger: 0.07, duration: 0.28, gain: 0.18 });

      const { x, y } = centreOf(target);

      for (let i = 0; i < STARS; i++) {
        const star = document.createElement('span');
        star.className = 'mod-feedback__star';

        // Spread over a full circle with a little jitter, so successive
        // celebrations do not produce an identical fan.
        const angle = (i / STARS) * Math.PI * 2 + Math.random() * 0.4;
        const distance = 70 + Math.random() * 90;

        star.style.left = `${x}px`;
        star.style.top = `${y}px`;
        star.style.setProperty('--dx', `${Math.cos(angle) * distance}px`);
        star.style.setProperty('--dy', `${Math.sin(angle) * distance}px`);
        star.style.animationDelay = `${Math.random() * 90}ms`;

        layer.append(star);
        // Self-removing rather than cleared in bulk, so overlapping
        // celebrations never cut each other short.
        window.setTimeout(() => star.remove(), STAR_LIFETIME_MS + 200);
      }

      target.classList.add('is-celebrating');
      window.setTimeout(() => target.classList.remove('is-celebrating'), 600);
    },

    tap() {
      if (disposed) return;
      // Short, quiet and mid-range: audible over a classroom without competing
      // with the narration, and deliberately not a chord - it is punctuation,
      // not applause.
      synth.tone({ semitone: 7, duration: 0.09, gain: 0.09, type: 'sine' });
    },

    nudge(target) {
      if (disposed) return;

      // Low, short and soft. Audible as "not that one" without being a buzzer.
      synth.tone({ semitone: -5, duration: 0.16, gain: 0.1, type: 'sine' });

      // Restart the animation reliably: without the reflow read, re-adding the
      // class in the same frame is coalesced away and nothing moves.
      target.classList.remove('is-nudging');
      void (target as HTMLElement).offsetWidth;
      target.classList.add('is-nudging');
      window.setTimeout(() => target.classList.remove('is-nudging'), 500);
    },

    destroy() {
      disposed = true;
      layer.remove();
      synth.destroy();
    },
  };
}
